const APP_CACHE_DB_NAME = 'ggc_app_cache';
const APP_CACHE_STORE_NAME = 'entries';
const APP_CACHE_DB_VERSION = 1;

/** 공통 IndexedDB 캐시 TTL (5분) */
export const INDEXED_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * How old an expired entry may be (measured from `updatedAt`) and still be served
 * stale-first by `runCachedFetch` when the caller opts into `staleWhileRevalidate`.
 * Chosen to absorb a normal working day's gaps (meetings, lunch, overnight is NOT
 * covered) — beyond it the entry counts as a miss and the caller blocks on the
 * network like a cold cache. Only affects callers that opt in; plain reads keep
 * blocking on the 5-minute TTL as before.
 */
export const INDEXED_CACHE_STALE_MAX_MS = 60 * 60 * 1000;

/** Remaining cache TTL as `m:ss` — header countdown, /settings, CachedApiTargetsList. */
export function formatCacheCountdownMs(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export interface IndexedDbCacheEntry<T> {
  key: string;
  value: T;
  updatedAt: number;
  expiresAt: number;
}

function isIndexedDbSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

let _cachedDb: IDBDatabase | null = null;

const OPEN_DB_TIMEOUT_MS = 3000;

function openAppCacheDb(): Promise<IDBDatabase> {
  if (_cachedDb) {
    try {
      // DB 연결이 유효한지 확인 (트랜잭션 생성 시도)
      _cachedDb.transaction(APP_CACHE_STORE_NAME, 'readonly');
      return Promise.resolve(_cachedDb);
    } catch {
      _cachedDb = null;
    }
  }

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('IndexedDB open timed out'));
    }, OPEN_DB_TIMEOUT_MS);

    const request = indexedDB.open(APP_CACHE_DB_NAME, APP_CACHE_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(APP_CACHE_STORE_NAME)) {
        db.createObjectStore(APP_CACHE_STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      db.onclose = () => { _cachedDb = null; };
      db.onversionchange = () => { db.close(); _cachedDb = null; };
      _cachedDb = db;
      resolve(db);
    };
    request.onerror = () => {
      clearTimeout(timer);
      reject(request.error);
    };
    request.onblocked = () => {
      clearTimeout(timer);
      reject(new Error('IndexedDB open blocked'));
    };
  });
}

export async function getIndexedDbCacheEntry<T>(key: string): Promise<IndexedDbCacheEntry<T> | null> {
  if (!key || !isIndexedDbSupported()) {
    return null;
  }

  try {
    const db = await openAppCacheDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readonly');
      const request = tx.objectStore(APP_CACHE_STORE_NAME).get(key);

      request.onsuccess = () => {
        resolve((request.result as IndexedDbCacheEntry<T> | undefined) ?? null);
      };
      request.onerror = () => {
        resolve(null);
      };
    });
  } catch {
    return null;
  }
}

export async function setIndexedDbCacheEntry<T>(key: string, value: T, ttlMs: number): Promise<void> {
  if (!key || !isIndexedDbSupported() || ttlMs <= 0) {
    return;
  }

  try {
    const db = await openAppCacheDb();
    const now = Date.now();
    const tx = db.transaction(APP_CACHE_STORE_NAME, 'readwrite');
    tx.objectStore(APP_CACHE_STORE_NAME).put({
      key,
      value,
      updatedAt: now,
      expiresAt: now + ttlMs,
    } satisfies IndexedDbCacheEntry<T>);
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // IndexedDB 실패 시 캐시 없이 계속 진행
  }
}

export async function clearIndexedDbCacheByPrefix(prefix: string): Promise<void> {
  if (!prefix || !isIndexedDbSupported()) {
    return;
  }

  try {
    const db = await openAppCacheDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(APP_CACHE_STORE_NAME);
      const request = store.openCursor();
      let resolved = false;

      const finish = () => {
        if (resolved) {
          return;
        }

        resolved = true;
        resolve();
      };

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          return;
        }

        if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) {
          cursor.delete();
        }
        cursor.continue();
      };

      request.onerror = finish;
      tx.oncomplete = finish;
      tx.onerror = finish;
      tx.onabort = finish;
    });

  } catch {
    // 캐시 삭제 실패는 치명적이지 않음
  }
}

/** Remove every entry in the app cache store (see `clearAllHeavyCaches` for why). */
export async function clearAllIndexedDbCacheEntries(): Promise<void> {
  if (!isIndexedDbSupported()) {
    return;
  }

  try {
    const db = await openAppCacheDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readwrite');
      tx.objectStore(APP_CACHE_STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // 캐시 삭제 실패는 치명적이지 않음
  }
}

/* ══════════════════════════════════════════════════════════
   Heavy API 캐시 레지스트리
   — 각 API 모듈이 registerHeavyCache()로 등록하면
     clearAllHeavyCaches / getHeavyCacheMinRemainingMs 가 자동으로 동작
   ══════════════════════════════════════════════════════════ */

interface HeavyCacheEntry {
  prefix: string;
  clear: () => Promise<void>;
  /** Optional: re-read this cache's well-known keys stale-first in the background (see `warmHeavyCaches`). */
  warm?: () => Promise<void>;
}

const _heavyCacheRegistry: HeavyCacheEntry[] = [];

/** 무거운 API 캐시를 레지스트리에 등록. 앱 초기화 시 각 API 모듈에서 호출. */
export function registerHeavyCache(
  prefix: string,
  clearFn: () => Promise<void>,
  warmFn?: () => Promise<void>,
): void {
  if (!_heavyCacheRegistry.some((e) => e.prefix === prefix)) {
    _heavyCacheRegistry.push({ prefix, clear: clearFn, warm: warmFn });
  }
}

/**
 * Proactive revalidation: ask every registered cache that knows its own well-known keys
 * (`warm`) to re-read them with `staleWhileRevalidate` + `background`. Fresh entries cost an
 * IndexedDB hit; expired ones refresh in the background *before* the user navigates to a
 * page that needs them, so even the stale-first swap mostly disappears. Never counts as a
 * blocked waiter. Only caches with a natural "everything" key opt in — per-entity caches
 * (panel.*) don't. Driven by `useHeavyCacheWarming` (visible tab + recent activity only).
 */
export async function warmHeavyCaches(): Promise<void> {
  await Promise.all(
    _heavyCacheRegistry.flatMap((e) =>
      e.warm
        ? [e.warm().catch(() => { /* best effort — the next read falls back to stale-first */ })]
        : [],
    ),
  );
}

/**
 * "Refresh now": drop every entry in the app cache store, not just the registered
 * prefixes. Registration happens on module import, and route-level code splitting
 * means e.g. `panel.api.ts` (5 caches, loaded with the panel chunk) isn't registered
 * until the user has opened a panel — a registry-only clear would silently leave
 * those entries behind. The store holds nothing but API caches, so a wholesale clear
 * is exactly "every cache target". Registered modules additionally get their in-flight
 * maps reset so a request that was mid-flight can't be joined after the clear.
 */
export async function clearAllHeavyCaches(): Promise<void> {
  await Promise.all(_heavyCacheRegistry.map((e) => e.clear()));
  await clearAllIndexedDbCacheEntries();
}

/**
 * Remaining time until the earliest expiry across every entry in the store (ms);
 * null when nothing unexpired is cached. Scans the whole store for the same reason
 * `clearAllHeavyCaches` does — the registry may not know every prefix yet.
 */
export async function getHeavyCacheMinRemainingMs(): Promise<number | null> {
  return getMinRemainingMsForKeyPrefixes(null);
}

/* ──────────────────── Heavy refresh tracker ────────────────────
 * Two counters for two different UI questions:
 *  - blocking waiters: callers currently awaiting a heavy network fetch with
 *    nothing usable in IndexedDB (cold miss, forceRefresh, or an expired entry
 *    read without stale-while-revalidate). Drives the "refreshing system
 *    cache" message in PageLoader, so it only shows when someone really waits.
 *  - in-flight refreshes: heavy network fetches running for any reason,
 *    background revalidations included. For "something is updating" cues
 *    (header refresh icon) that must not imply the user is blocked.
 * Both are maintained by `runCachedFetch`; nothing else should touch them. */
let _blockingHeavyWaiters = 0;
let _inFlightHeavyRefreshes = 0;
const _heavyRefreshSubscribers: Set<() => void> = new Set();

function notifyHeavyRefresh(): void {
  for (const cb of _heavyRefreshSubscribers) cb();
}

function beginBlockingWait(): void {
  _blockingHeavyWaiters += 1;
  notifyHeavyRefresh();
}

function endBlockingWait(): void {
  _blockingHeavyWaiters = Math.max(0, _blockingHeavyWaiters - 1);
  notifyHeavyRefresh();
}

function beginInFlightRefresh(): void {
  _inFlightHeavyRefreshes += 1;
  notifyHeavyRefresh();
}

function endInFlightRefresh(): void {
  _inFlightHeavyRefreshes = Math.max(0, _inFlightHeavyRefreshes - 1);
  notifyHeavyRefresh();
}

/** Callers blocked on a heavy fetch right now (no usable cache to show). */
export function getBlockingHeavyRefreshCount(): number {
  return _blockingHeavyWaiters;
}

/* When a page (not a background job) last got a stale-first value — i.e. it painted from an
 * expired entry and a swap is coming. Lets the CacheRefreshIndicator explain that swap
 * without also flagging proactive warming on pages that never showed stale data. */
let _lastStaleServedAt = 0;

export function getLastStaleServedAt(): number {
  return _lastStaleServedAt;
}

/** Heavy network fetches in flight right now — blocking or background. */
export function getInFlightHeavyRefreshCount(): number {
  return _inFlightHeavyRefreshes;
}

/** Fires whenever either counter changes. `useSyncExternalStore`-friendly. */
export function subscribeHeavyRefresh(cb: () => void): () => void {
  _heavyRefreshSubscribers.add(cb);
  return () => _heavyRefreshSubscribers.delete(cb);
}

/* ──────────────────── Cache-updated notifications ────────────────────
 * Fired after a fetch writes fresh data for `prefix` to IndexedDB, so a
 * component that renders from a cache it doesn't own (e.g. a sidebar reading
 * the campaign list) can re-read — the re-read is a cache hit, so it's cheap. */
const _cacheUpdatedSubscribers: Set<(prefix: string) => void> = new Set();

export function subscribeCacheUpdated(cb: (prefix: string) => void): () => void {
  _cacheUpdatedSubscribers.add(cb);
  return () => _cacheUpdatedSubscribers.delete(cb);
}

function notifyCacheUpdated(prefix: string): void {
  for (const cb of _cacheUpdatedSubscribers) cb(prefix);
}

/* ══════════════════════════════════════════════════════════
   runCachedFetch — shared read-through core
   Every IndexedDB-backed API (createCachedApi below and the hand-rolled
   campaign/advertiser caches) goes through this, so cache semantics live in
   exactly one place.

   IDB state                                  | behavior
   -------------------------------------------|------------------------------------------
   fresh  (expiresAt > now)                   | return cached value
   stale  (expired, updatedAt ≤ STALE_MAX)    | + staleWhileRevalidate → return stale now,
                                              |   refresh in background, `revalidation`
                                              |   resolves with the fresh value
                                              | otherwise → block on network
   miss   (absent, or older than STALE_MAX)   | block on network
   forceRefresh                               | skip the read, block on network

   A blocking fetch that fails falls back to whatever entry exists (any age) —
   the long-standing behavior — and throws only when there is none. Concurrent
   callers share one network request per key via the caller-owned `pending` map.
   ══════════════════════════════════════════════════════════ */

export interface CachedFetchOptions {
  /** Skip the cache read and hit the network. */
  forceRefresh?: boolean;
  /**
   * Serve an expired-but-present entry immediately and refresh in the background.
   * Default false — callers that don't opt in keep blocking on the 5-minute TTL.
   */
  staleWhileRevalidate?: boolean;
  /**
   * The caller is itself a background job (poll, revalidation pass): its wait never
   * counts as a blocked user, so the PageLoader message stays off.
   */
  background?: boolean;
}

export interface CachedFetchResult<T> {
  value: T;
  /** True when `value` came from an expired entry (stale-first, or network-error fallback). */
  stale: boolean;
  /**
   * Fresh data on its way — non-null only for a stale-first result. Never rejects:
   * a failed revalidation resolves with the stale value again.
   */
  revalidation: Promise<T> | null;
}

export interface RunCachedFetchArgs<T> {
  key: string;
  /** Cache key prefix — reported to `subscribeCacheUpdated` listeners after a write. */
  prefix: string;
  /** Caller-owned in-flight map so concurrent callers share one network request per key. */
  pending: Map<string, Promise<T>>;
  fetcher: () => Promise<T>;
  /** Applied to network responses and cached values alike (default: identity). */
  normalize?: (value: T) => T;
  /** Cache write condition (default: truthy). */
  shouldCache?: (value: T) => boolean;
  /** Counted in the heavy-refresh trackers (default: true). */
  heavy?: boolean;
  options?: CachedFetchOptions;
}

type CacheReadState = 'fresh' | 'stale' | 'miss';

function classifyCacheEntry(entry: IndexedDbCacheEntry<unknown> | null, now: number): CacheReadState {
  if (!entry) return 'miss';
  if (entry.expiresAt > now) return 'fresh';
  return now - entry.updatedAt <= INDEXED_CACHE_STALE_MAX_MS ? 'stale' : 'miss';
}

/**
 * Start — or join — the single network fetch for `key`. Rejects on failure so each
 * caller can apply its own fallback (blocking → old entry, stale-first → stale value).
 */
function startNetworkFetch<T>(args: {
  key: string;
  prefix: string;
  pending: Map<string, Promise<T>>;
  fetcher: () => Promise<T>;
  normalize: (value: T) => T;
  shouldCache: (value: T) => boolean;
  heavy: boolean;
}): Promise<T> {
  const { key, prefix, pending, fetcher, normalize, shouldCache, heavy } = args;
  const inflight = pending.get(key);
  if (inflight) return inflight;

  const request = (async () => {
    if (heavy) beginInFlightRefresh();
    try {
      const data = normalize(await fetcher());
      if (shouldCache(data)) {
        await setIndexedDbCacheEntry(key, data, INDEXED_CACHE_TTL_MS);
        notifyCacheUpdated(prefix);
      }
      return data;
    } finally {
      pending.delete(key);
      if (heavy) endInFlightRefresh();
    }
  })();

  pending.set(key, request);
  return request;
}

export async function runCachedFetch<T>(args: RunCachedFetchArgs<T>): Promise<CachedFetchResult<T>> {
  const { key, prefix, pending, fetcher, options = {} } = args;
  const normalize = args.normalize ?? ((value: T) => value);
  const shouldCache = args.shouldCache ?? Boolean;
  const heavy = args.heavy ?? true;

  const entry = options.forceRefresh ? null : await getIndexedDbCacheEntry<T>(key);
  const state = classifyCacheEntry(entry, Date.now());

  if (entry && state === 'fresh') {
    return { value: normalize(entry.value), stale: false, revalidation: null };
  }

  const servingStaleFirst = !!entry && state === 'stale' && !!options.staleWhileRevalidate;
  // Mark "a page just painted stale data" *before* the fetch starts: the in-flight increment
  // below notifies tracker subscribers synchronously, and CacheRefreshIndicator must see the
  // stale-served timestamp in that same pass (a later change alone wouldn't re-render it).
  if (servingStaleFirst && !options.background) {
    _lastStaleServedAt = Date.now();
  }

  const request = startNetworkFetch({ key, prefix, pending, fetcher, normalize, shouldCache, heavy });

  if (servingStaleFirst && entry) {
    const staleValue = normalize(entry.value);
    return {
      value: staleValue,
      stale: true,
      revalidation: request.catch((error: unknown) => {
        console.warn(`[indexedDbCache] background revalidation failed for ${key}; keeping stale value`, error);
        return staleValue;
      }),
    };
  }

  // Nothing usable to show (miss, forceRefresh, or stale without opt-in): the caller waits.
  const countsAsBlocked = heavy && !options.background;
  if (countsAsBlocked) beginBlockingWait();
  try {
    return { value: await request, stale: false, revalidation: null };
  } catch (error) {
    if (entry) return { value: normalize(entry.value), stale: true, revalidation: null };
    throw error;
  } finally {
    if (countsAsBlocked) endBlockingWait();
  }
}

/* ══════════════════════════════════════════════════════════
   createCachedApi — 캐시 패턴 공통화 헬퍼
   사용 예:
     const { fetch, clear, CACHE_KEY_PREFIX } = createCachedApi<MyData[]>({
       prefix: 'myApi.getData::',
       fetcher: () => webApi3.post('myApi.getData', {}),
       normalize: (data) => Array.isArray(data) ? data : [],
     });
   ══════════════════════════════════════════════════════════ */

export interface CachedApiOptions<T> {
  /** IndexedDB 캐시 키 접두사 (예: 'campaigns.getCampaigns::') */
  prefix: string;
  /** 실제 API 호출 함수 */
  fetcher: (params: Record<string, unknown>) => Promise<T>;
  /** 응답 정규화 (기본: identity) */
  normalize?: (data: T) => T;
  /** 캐시 저장 조건 (기본: truthy) */
  shouldCache?: (data: T) => boolean;
  /** heavy 캐시 레지스트리에 등록 여부 (기본: true) */
  heavy?: boolean;
  /**
   * Params sets to re-read during proactive warming (`warmHeavyCaches`), e.g. `[{}]` for a
   * list endpoint. Omit for per-entity caches whose keys depend on what the user opened.
   */
  warmParams?: Record<string, unknown>[];
}

export interface CachedApi<T> {
  /** 캐시 키 접두사 */
  CACHE_KEY_PREFIX: string;
  /**
   * 캐시 우선 조회, 미스 시 API 호출 후 캐시 저장 (값만 반환).
   * `staleWhileRevalidate`를 주면 만료된 값을 즉시 돌려주고 갱신은 백그라운드에서 진행된다.
   */
  fetch: (params?: Record<string, unknown>, options?: CachedFetchOptions) => Promise<T>;
  /** `fetch`와 같은 조회 + `stale` / `revalidation` 메타 — 갱신 도착 시 데이터를 교체할 호출자용 */
  fetchWithMeta: (
    params?: Record<string, unknown>,
    options?: CachedFetchOptions,
  ) => Promise<CachedFetchResult<T>>;
  /** 이 API의 캐시만 삭제 */
  clear: () => Promise<void>;
}

export function createCachedApi<T>(opts: CachedApiOptions<T>): CachedApi<T> {
  const { prefix, fetcher, normalize, shouldCache, heavy = true, warmParams } = opts;
  const pending = new Map<string, Promise<T>>();

  function cacheKey(params: Record<string, unknown>): string {
    return `${prefix}${JSON.stringify(getStableCacheValue(params))}`;
  }

  async function clear(): Promise<void> {
    pending.clear();
    await clearIndexedDbCacheByPrefix(prefix);
  }

  function fetchWithMeta(
    params: Record<string, unknown> = {},
    options: CachedFetchOptions = {},
  ): Promise<CachedFetchResult<T>> {
    return runCachedFetch<T>({
      key: cacheKey(params),
      prefix,
      pending,
      fetcher: () => fetcher(params),
      normalize,
      shouldCache,
      heavy,
      options,
    });
  }

  async function fetchFn(
    params: Record<string, unknown> = {},
    options: CachedFetchOptions = {},
  ): Promise<T> {
    return (await fetchWithMeta(params, options)).value;
  }

  if (heavy) {
    registerHeavyCache(
      prefix,
      clear,
      warmParams
        ? async () => {
            await Promise.all(
              warmParams.map((params) =>
                fetchWithMeta(params, { staleWhileRevalidate: true, background: true }),
              ),
            );
          }
        : undefined,
    );
  }

  return { CACHE_KEY_PREFIX: prefix, fetch: fetchFn, fetchWithMeta, clear };
}

/**
 * Recursively sort object keys (dropping `undefined` values) so `JSON.stringify`
 * yields the same cache key regardless of property order. Shared by every cache
 * key builder — was duplicated in campaign.api.ts / advertiser.api.ts before.
 */
export function getStableCacheValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(getStableCacheValue);
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const v = (value as Record<string, unknown>)[key];
        if (v !== undefined) acc[key] = getStableCacheValue(v);
        return acc;
      }, {});
  }
  return value;
}

/** Visit every entry in the store. Resolves true when the scan completed, false on any error. */
async function scanAppCacheEntries(
  visit: (key: string, entry: IndexedDbCacheEntry<unknown>) => void,
): Promise<boolean> {
  if (!isIndexedDbSupported()) {
    return false;
  }

  try {
    const db = await openAppCacheDb();
    return await new Promise<boolean>((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readonly');
      const request = tx.objectStore(APP_CACHE_STORE_NAME).openCursor();

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          return;
        }
        if (typeof cursor.key === 'string') {
          visit(cursor.key, cursor.value as IndexedDbCacheEntry<unknown>);
        }
        cursor.continue();
      };

      request.onerror = () => resolve(false);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    });
  } catch {
    return false;
  }
}

/**
 * 지정한 키 접두사에 해당하는 항목 중, 아직 만료 전인 것만 보고
 * 가장 빨리 만료되는 시점까지 남은 시간(ms)을 반환한다. 해당 항목이 없으면 null.
 * `prefixes`가 null이면 저장소의 모든 항목을 대상으로 한다.
 */
export async function getMinRemainingMsForKeyPrefixes(
  prefixes: readonly string[] | null,
): Promise<number | null> {
  if (prefixes !== null && !prefixes.length) {
    return null;
  }

  const now = Date.now();
  const acc: { best: number | null } = { best: null };
  const completed = await scanAppCacheEntries((key, entry) => {
    if (prefixes !== null && !prefixes.some((p) => key.startsWith(p))) return;
    if (typeof entry?.expiresAt !== 'number' || entry.expiresAt <= now) return;
    const rem = entry.expiresAt - now;
    if (acc.best === null || rem < acc.best) {
      acc.best = rem;
    }
  });
  return completed ? acc.best : null;
}

export interface AppCachePrefixSummary {
  prefix: string;
  /** Entries stored under this prefix (one per distinct params key). */
  entries: number;
  /** Time until the earliest unexpired entry under this prefix expires; null when all are expired. */
  minRemainingMs: number | null;
}

/**
 * What is actually cached right now, grouped by key prefix (`<api>::<params>`). For
 * the header/settings "affected caches" lists — derived from the store, so it can't
 * drift from the code the way a hard-coded list does.
 */
export async function getAppCachePrefixSummaries(): Promise<AppCachePrefixSummary[]> {
  const now = Date.now();
  const byPrefix = new Map<string, AppCachePrefixSummary>();
  await scanAppCacheEntries((key, entry) => {
    const sep = key.indexOf('::');
    const prefix = sep >= 0 ? key.slice(0, sep + 2) : key;
    const summary = byPrefix.get(prefix) ?? { prefix, entries: 0, minRemainingMs: null };
    summary.entries += 1;
    if (typeof entry?.expiresAt === 'number' && entry.expiresAt > now) {
      const rem = entry.expiresAt - now;
      if (summary.minRemainingMs === null || rem < summary.minRemainingMs) {
        summary.minRemainingMs = rem;
      }
    }
    byPrefix.set(prefix, summary);
  });
  return [...byPrefix.values()].sort((a, b) => a.prefix.localeCompare(b.prefix));
}
